import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import html2canvas from "html2canvas";
import "./warehouse-map.css";
import { DepotGroup, FillPct, Loc } from "./types";
import { groupByCladire, nat } from "./utils";
import DepotSection from "./DepotSection";
import Toolbar from "./Toolbar";
import FiltersBar from "./FiltersBar";
import { useDebounce } from "../../hooks/useDebounce";

type Props = {
  locations: any[];
  defaultDepot?: string;
  selectedDepot?: string;
  onDepotChange?: (v: string) => void;
  className?: string;
};

function sameArr(a: string[], b: string[]) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export const WarehouseMap: React.FC<Props> = ({
  locations,
  defaultDepot = "ALL",
  selectedDepot: selectedDepotProp,
  onDepotChange,
  className,
}) => {
  const groups: DepotGroup[] = useMemo(
    () => groupByCladire(locations),
    [locations],
  );
  const depotIds = useMemo(() => groups.map((g) => g.cladire), [groups]);

  const isControlled = typeof selectedDepotProp !== "undefined";
  const [internalDepot, setInternalDepot] = useState<string>(defaultDepot);
  const selectedDepot = isControlled
    ? (selectedDepotProp as string)
    : internalDepot;

  const setSelectedDepot = useCallback(
    (v: string) => {
      if (isControlled) onDepotChange?.(v);
      else setInternalDepot((prev) => (prev === v ? prev : v));
    },
    [isControlled, onDepotChange],
  );

  const [cellSize, _setCellSize] = useState(16);
  const setCellSize = useCallback((n: number) => {
    _setCellSize((prev) => (prev === n ? prev : n));
  }, []);

  const [highlight, _setHighlight] = useState<FillPct | null>(null);
  const setHighlight = useCallback((v: FillPct | null) => {
    _setHighlight((prev) => (prev === v ? prev : v));
  }, []);

  const debouncedHighlight: any = useDebounce(highlight, 120);
  const effectiveHighlight: FillPct | null =
    highlight === null ? null : debouncedHighlight;

  const [exporting, setExporting] = useState<null | "png" | "pdf">(null);

  const resolveNivel = useCallback(
    (l: Loc): FillPct => l.nivelUmplere as FillPct,
    [],
  );

  const styleVars = useMemo(
    () =>
      ({ ["--wm-cell-size" as any]: `${cellSize}px` } as React.CSSProperties),
    [cellSize],
  );

  const visible = useMemo(
    () =>
      selectedDepot === "ALL"
        ? groups
        : groups.filter((g) => g.cladire === selectedDepot),
    [groups, selectedDepot],
  );

  const captureRef = useRef<HTMLDivElement>(null);

  const exportPNG = async () => {
    const root = captureRef.current;
    if (!root) return;
    setExporting("png");
    root.classList.add("wm-exporting");
    try {
      const canvas = await html2canvas(root, {
        backgroundColor: "#ffffff",
        scale: Math.min(2, Math.max(1, window.devicePixelRatio || 1)),
        useCORS: true,
        windowWidth: root.scrollWidth,
        windowHeight: root.scrollHeight,
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `warehouse-map-${Date.now()}.png`;
      a.click();
    } catch (err) {
      console.error("PNG export failed:", err);
    } finally {
      root.classList.remove("wm-exporting");
      setExporting(null);
    }
  };

  const exportPDF = async () => {
    const root = captureRef.current;
    if (!root || typeof window === "undefined") return;
    setExporting("pdf");
    root.classList.add("wm-exporting");
    try {
      const { jsPDF } = await import("jspdf");
      const scale = Math.min(1.3, window.devicePixelRatio || 1);
      const full = await html2canvas(root, {
        backgroundColor: "#ffffff",
        scale,
        useCORS: true,
        allowTaint: true,
        windowWidth: root.scrollWidth,
        windowHeight: root.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      const fullW = full.width;
      const fullH = full.height;
      const orientation: "p" | "l" = fullW > fullH ? "l" : "p";

      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation });

      const margin = 10;
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const contentW = pageW - 2 * margin;
      const contentH = pageH - 2 * margin;

      const mmPerPx = contentW / fullW;
      const maxTilePxH = Math.floor(contentH / mmPerPx);
      const tileSrcPxH = Math.min(1600, Math.max(1, maxTilePxH));
      const outWpx = Math.min(2200, fullW);
      const scaleDown = outWpx / fullW;

      const tileCanvas = document.createElement("canvas");
      const tctx = tileCanvas.getContext("2d")!;
      tileCanvas.width = outWpx;

      let srcY = 0;
      let yMmCursor = margin;

      while (srcY < fullH) {
        const srcH = Math.min(tileSrcPxH, fullH - srcY);
        const outHpx = Math.max(1, Math.round(srcH * scaleDown));
        tileCanvas.height = outHpx;
        tctx.clearRect(0, 0, outWpx, outHpx);
        tctx.drawImage(full, 0, srcY, fullW, srcH, 0, 0, outWpx, outHpx);

        const dataURL = tileCanvas.toDataURL("image/jpeg", 0.9);
        const tileMmH = srcH * mmPerPx;

        if (yMmCursor + tileMmH > pageH - margin) {
          pdf.addPage();
          yMmCursor = margin;
        }

        pdf.addImage(
          dataURL,
          "JPEG",
          margin,
          yMmCursor,
          contentW,
          tileMmH,
          undefined,
          "FAST",
        );
        yMmCursor += tileMmH;
        srcY += srcH;
      }

      pdf.save(`warehouse-map-${Date.now()}.pdf`);
      tileCanvas.width = tileCanvas.height = 0;
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      root.classList.remove("wm-exporting");
      setExporting(null);
    }
  };

  const { rOptions, kOptions, pOptions } = useMemo(() => {
    const r = new Set<string>(),
      k = new Set<string>(),
      p = new Set<string>();
    for (const g of visible)
      for (const l of g.data) {
        r.add(l.rand);
        k.add(l.coloana);
        p.add(l.polita);
      }
    return {
      rOptions: Array.from(r),
      kOptions: Array.from(k),
      pOptions: Array.from(p),
    };
  }, [visible]);

  const [selR, _setSelR] = useState<string[]>([]);
  const [selK, _setSelK] = useState<string[]>([]);
  const [selP, _setSelP] = useState<string[]>([]);

  const setSelR = useCallback((next: string[]) => {
    _setSelR((prev) => (sameArr(prev, next) ? prev : next));
  }, []);
  const setSelK = useCallback((next: string[]) => {
    _setSelK((prev) => (sameArr(prev, next) ? prev : next));
  }, []);
  const setSelP = useCallback((next: string[]) => {
    _setSelP((prev) => (sameArr(prev, next) ? prev : next));
  }, []);

  useEffect(() => {
    const next = selR.filter((v) => rOptions.includes(v));
    if (!sameArr(selR, next)) setSelR(next);
  }, [rOptions, selR, setSelR]);

  useEffect(() => {
    const next = selK.filter((v) => kOptions.includes(v));
    if (!sameArr(selK, next)) setSelK(next);
  }, [kOptions, selK, setSelK]);

  useEffect(() => {
    const next = selP.filter((v) => pOptions.includes(v));
    if (!sameArr(selP, next)) setSelP(next);
  }, [pOptions, selP, setSelP]);

  const clearFilters = useCallback(() => {
    setSelR([]);
    setSelK([]);
    setSelP([]);
  }, [setSelR, setSelK, setSelP]);

  const allCodes = useMemo(() => {
    const set = new Set<string>();
    for (const l of locations) set.add(l.locatie);
    return Array.from(set).sort(nat);
  }, [locations]);

  const onJump = useCallback((code: string) => {
    const el = document.querySelector(
      `[data-cell="${code}"]`,
    ) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
    el.classList.add("wm-pulse");
    setTimeout(() => el.classList.remove("wm-pulse"), 900);
  }, []);

  const filtersMemo = useMemo(
    () => ({ r: selR, k: selK, p: selP }),
    [selR, selK, selP],
  );

  return (
    <div className={`wm-page ${className || ""}`} style={styleVars}>
      <Toolbar
        depotIds={depotIds}
        selectedDepot={selectedDepot}
        onChangeDepot={setSelectedDepot}
        cellSize={cellSize}
        setCellSize={setCellSize}
        highlight={highlight}
        setHighlight={setHighlight}
        onExportPNG={exportPNG}
        onExportPDF={exportPDF}
        exporting={exporting}
        showDepotPicker={false}
      />

      <FiltersBar
        depotIds={depotIds}
        selectedDepot={selectedDepot}
        onChangeDepot={setSelectedDepot}
        rOptions={rOptions}
        kOptions={kOptions}
        pOptions={pOptions}
        selR={selR}
        selK={selK}
        selP={selP}
        setSelR={setSelR}
        setSelK={setSelK}
        setSelP={setSelP}
        onClear={clearFilters}
        codes={allCodes}
        onJump={onJump}
      />

      <div ref={captureRef} className="wm-capture">
        {visible.map((g) => (
          <DepotSection
            key={g.cladire}
            group={g}
            highlight={effectiveHighlight}
            filters={filtersMemo}
            resolveNivel={resolveNivel}
          />
        ))}
      </div>

      {exporting && (
        <div className="wm-page-overlay" aria-hidden="true">
          <div className="wm-page-spinner" />
          <div className="wm-page-loading-text">
            {exporting === "png" ? "Generare PNG…" : "Generare PDF…"}
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseMap;
